<template>
  <div id="main-page">
    <div class="container">
      <app-bar
        title="Pilih Poli"
        previous-link="/" />

      <div class="row">
        <div class="col-md-4">
          <div class="input-group mb-3 rounded-pill">
            <input
              type="text"
              class="form-control bg-transparent"
              placeholder="Cari poli..."
              name="search"
              v-model="keyword"
              autocomplete="off">
            <span class="input-group-text bg-transparent border-0">
              <div
                class="box-icon"
                type="button"
                @click="keyword = ''"
              >
                <i class="ri-close-circle-fill fs-14 color-neutral-700"></i>
              </div>
            </span>
          </div>
        </div>
      </div>

      <Loading v-if="loading.listPoli && filteredPoli.length < 1" />
      <div v-else-if="filteredPoli.length">
        <div class="row g-3 g-md-4">
          <div
            v-for="poli in filteredPoli"
            :key="poli.poli_kode || poli.poli_nama"
            class="col-6 col-md-4">
            <button type="button" class="poli-card w-100" @click="selectPoli(poli)">
              <div class="d-flex align-items-center justify-content-between">
                <div class="me-3 flex-grow-1">
                  <div class="fw-700 fs-14 color-neutral-900">{{ poli.poli_header || poli.poli_nama }}</div>
                  <div v-if="poli.poli_deskripsi" class="fw-400 fs-12 color-neutral-600 mt-1">{{ poli.poli_deskripsi }}</div>
                </div>
                <div class="icon-rect d-flex align-items-center justify-content-center bg-neutral-200 rounded">
                  <img :src="poli.poli_icon" alt="poli icon" />
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
      <div v-else class="not-found d-flex flex-column text-center pb-5 pt-5">
        <div class="image-wrapper">
          <img src="@/assets/icon/empty.png" alt="">
        </div>
        <p class="text-title-active fw-bold fs-18 lh-28">Tidak ada hasil ditemukan</p>
        <div class="text-body-light d-flex justify-content-center">
          <p class="fw-400 fs-14 lh-24 mb-0">Maaf poli yang anda cari tidak ditemukan.</p>
        </div>
      </div>
    </div>
    <div class="bottom-bar pb-md-3 p-2 px-md-0" v-if="filteredPoli.length">
      <div class="container">
        <div class="row">
          <div class="col-md-4">
            <router-link
              :to="{ name: 'ProfilOrder' }"
              class="btn btn-primary text-white w-100 pt-2 pb-2">
              LIHAT RIWAYAT
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import Loading from '@/components/utility/Loading'
import { listPoli, getListPoli, loading, selectedPoli } from '@/core/poli'
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { showNotif } from '@/core/utility'

export default {
  components: {
    AppBar, Loading
  },
  setup () {
    const keyword = ref('')
    const router = useRouter()

    const parentPoli = ref(null)

    const filteredPoli = computed(() => {
      // If a parent poli is selected and it has details, show the details list
      if (parentPoli.value && Array.isArray(parentPoli.value.detail) && parentPoli.value.detail.length) {
        const parentIcon = parentPoli.value.poli_icon
        const mappedDetails = parentPoli.value.detail.map((detail) => ({
          poli_kode: detail.poli_kode,
          poli_nama: detail.poli_nama,
          poli_icon: parentIcon
        }))

        return mappedDetails.filter((data) => {
          return keyword.value.toLowerCase().split(' ').every(v => (data.poli_nama || '').toLowerCase().includes(v))
        })
      }

      // Default: show top-level poli list
      return listPoli.value ? listPoli.value.filter((data) => {
        return keyword.value.toLowerCase().split(' ').every(v => (data.poli_nama || '').toLowerCase().includes(v))
      }) : []
    })

    const selectPoli = (poli) => {
      // If not in detail mode yet, and poli has details, switch to detail list
      if (!parentPoli.value) {
        if (Array.isArray(poli.detail) && poli.detail.length) {
          parentPoli.value = poli
          return
        }

        // No details available, show notification
        return showNotif({
          status: 'error',
          message: 'Detail poli tidak tersedia'
        })
      }

      // In detail mode: set selectedPoli using detail's poli_kode and poli_nama, inherit others from parent
      selectedPoli.value = {
        ...parentPoli.value,
        poli_kode: poli.poli_kode,
        poli_nama: poli.poli_nama
      }

      // lanjut ke page selanjutnya
      router.push({
        name: 'CheckoutPoli',
        query: { poli: poli.poli_kode }
      })
    }

    onMounted(() => {
      getListPoli()
    })

    return {
      keyword,
      loading,
      filteredPoli,
      parentPoli,
      selectPoli
    }
  }
}
</script>

<style lang="scss" scoped>
#main-page {
  @media (max-width: 520px) {
    padding-bottom: 3.25rem;
  }
}

.input-group {
  border: 1px solid #E2E8F0;

  .form-control, .input-group-text {
    height: 34px;
  }

  .form-control:focus{
    box-shadow: none;
  }

  .form-control::placeholder {
    color: #A0AEC0;
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
  }
}

.poli-card {
  background-color: #fff;
  border: 1.5px solid #CEE4EA;
  border-radius: 14px;
  padding: 14px 16px;
  text-align: left;
}

.poli-card:focus {
  outline: none;
  box-shadow: none;
}

.icon-rect {
  width: 42px;
  height: 42px;
}

.icon-rect img {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.not-found {
  .image-wrapper {
    margin-bottom: 24px;
  }

  .text-title-active {
    margin-bottom: 4px;
  }
}

.bottom-bar {
  @media (max-width: 520px) {
    background-color: white;
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    border-top: 1px solid rgb(240, 240, 240);
  }
}
</style>
